import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  FormControlLabel,
  IconButton,
  ListItemButton,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { sv } from "date-fns/locale";
import React, { useEffect } from "react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  isNumber,
  overChargingTransactions,
  transactionsAlreadyExists,
  validateMissingCareInfo,
} from "../../util/validators";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { MODAL_CONFIRM } from "../../redux/reducers/modalReducer";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  HSAUnit,
  RegisterTransactionDTO,
  TransactionDTO,
} from "../../generated/api";
import {
  addStaticTimeToDate,
  dateFormatter,
  formatShortDate,
  isShortDate,
} from "../../util/formats";
import { INVALID_DATE } from "../../util/constants";
import { REGISTER_TRANSACTIONS } from "../../redux/reducers/transactionReducer";
import { RESET_HSA, SEARCH_HSA_UNITS } from "../../redux/reducers/hsaReducer";
import HelpIcon from "@mui/icons-material/Help";

interface Iamount {
  careProvider: string;
  date: string;
  amount: number;
  careUnit: string;
}

export default function AddMultipleTransactions() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const {
    register,
    unregister,
    getValues,
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const searchCardForm = useSelector((state: any) => state.card.params);
  const cardTypes = useSelector((state: any) => state.cardTypes.cardTypes);
  const currentUser = useSelector((state: any) => state.session.currentUser);
  const careProviders = useSelector(
    (state: any) => state.hsaUnits.careProviders
  );
  const careUnits = useSelector((state: any) => state.hsaUnits.careUnits);
  const loading = useSelector((state: any) => state.loadingState.loading);
  const carePrincipalId = useSelector(
    (state: any) => state.session.selectedCarePrincipal.id
  );

  const [values, setValues] = useState<string[]>([
    new Date().toLocaleDateString(),
  ]);
  const [dateValues, setDateValues] = useState<string[]>([
    new Date().toLocaleDateString(),
  ]);
  const [selectedCareUnits, setSelectedCareUnits] = useState<HSAUnit[]>([]);
  const [selectedCareProviders, setSelectedCareProviders] = useState<HSAUnit[]>(
    []
  );
  const [careproviderSearch, setCareproviderSearch] = useState("");
  const [warningMessages, setWarningMessages] = useState<string[]>([]);
  const [rowWarnings, setRowWarnings] = useState<number[]>([]);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [rowErrors, setRowErrors] = useState<number[]>([]);
  const extendedCard = useSelector((state: any) => state.card.extendedCard);
  const [inputList, setInputList] = useState<Iamount[]>([
    {
      careProvider: "",
      date: new Date().toLocaleDateString(),
      amount: 0,
      careUnit: "null",
    },
  ]);
  const [errorText, setErrorText] = useState({
    carePrincipal: "",
    date: "",
    amount: "",
  });
  const cardType = cardTypes.find(
    (o: any) => o.id === searchCardForm.cardTypeId
  );
  const [ignoreWarning, setIgnoreWarning] = useState(false);

  const careProvider = "careProvider";
  const amount = "amount";

  useEffect(() => {
    setErrorText(errorText);
  }, [errorText]);

  const handleRemoveClick = (index: any) => {
    const list = [...inputList];
    list.splice(index, 1);
    setInputList(list);

    //Remind ourselves to never use controllers, this is messy
    for (let i = index; i < inputList?.length; i++) {
      setValue("amount" + i, getValues(amount + (i + 1)));
    }
    unregister("amount" + inputList?.length);

    let dates = [...values];
    dates.splice(index, 1);
    setValues(dates);

    dates = [...dateValues];
    dates.splice(index, 1);
    setDateValues(dates);

    const careproviders = [...selectedCareProviders];
    careproviders.splice(index, 1);
    setSelectedCareProviders(careproviders);

    const careUnits = [...selectedCareUnits];
    careUnits.splice(index, 1);
    setSelectedCareUnits(careUnits);
  };

  const handleAddClick = () => {
    setInputList([
      ...inputList,
      {
        careProvider: "",
        date: new Date().toLocaleDateString(),
        amount: 0,
        careUnit: "",
      },
    ]);
    setDateValues([...dateValues, new Date().toLocaleDateString()]);
    setValues([...values, new Date().toLocaleDateString()]);
  };

  function validate(transactions: TransactionDTO[]) {
    let warn = false;
    let warnings: string[] = [];

    if (overChargingTransactions(extendedCard, transactions)) {
      warn = true;
      warnings = [...warnings, t("warn.sum-overcharged")];
    }
    let rowWarnings = transactionsAlreadyExists(extendedCard, transactions);

    rowWarnings.forEach((index) => {
      warn = true;
      warnings = [
        ...warnings,
        t("warn.several-existing", {
          row: index + 1,
          amount: transactions[index].amount,
        }),
      ];
    });
    setRowWarnings(rowWarnings);

    setWarningMessages(warnings);
    return warn;
  }

  function validateError(transactions: TransactionDTO[]) {
    let error = false;
    let errors: string[] = [];
    const careRowErrors = validateMissingCareInfo(transactions);

    careRowErrors.forEach((index) => {
      error = true;
      errors = [
        ...errors,
        t("errors.missing-care-info", {
          row: index + 1,
        }),
      ];
    });
    setRowErrors(careRowErrors);
    setErrorMessages(errors);

    return error;
  }

  const onSubmit = (values: any) => {
    const transactionArray = [];

    for (let i = 0; i < inputList.length; i++) {
      let transaction = {
        careProviderId: selectedCareProviders[i]?.hsaIdentity,
        careProvider: selectedCareProviders[i]?.name,
        careUnitId: selectedCareUnits[i]?.hsaIdentity,
        careUnit: selectedCareUnits[i]?.name,
        transactionDate: addStaticTimeToDate(new Date(dateValues[i])),
        amount: parseInt(values["amount" + i]),
      };
      transactionArray.push(transaction);
    }

    let registration = {
      actorId: currentUser.employeeHsaId,
      actorName: currentUser.name,
      cardTypeId: searchCardForm.cardTypeId,
      careUnitId: currentUser.healthCareUnitHsaId,
      careProviderId: currentUser.healthCareProviderHsaId,
      carePrincipalId: carePrincipalId,
      personId: searchCardForm.personId,
      transactionParts: transactionArray,
      ignoreWarning: ignoreWarning,
    };
    if (
      (ignoreWarning || !validate(transactionArray)) &&
      !validateError(transactionArray)
    ) {
      // eslint-disable-next-line
      if (!dateValues.some((value) => value == INVALID_DATE)) {
        dispatch({ type: REGISTER_TRANSACTIONS, registration, cardType });
        dispatch({ type: MODAL_CONFIRM });
      }
    }
  };

  function handleChangeWarning(event: any) {
    setIgnoreWarning(event.target.checked);
  }

  function onDateChange(date: string | null, index: number) {
    // eslint-disable-next-line
    if (date != INVALID_DATE) {
      let dates = [...dateValues];
      if (date) {
        dates[index] = date;
      }
      setDateValues(dates);
    }
  }

  function onTextChange(event: any, index: number) {
    const date = event.target.value;
    let vals = [...values];
    let dateVals = [...dateValues];
    if (isShortDate(date)) {
      const dateFormat = formatShortDate(date);
      vals[index] = dateFormat;
      dateVals[index] = dateFormat;
      setValues(vals);
      setDateValues(dateVals);
    } else {
      vals[index] = date;
      dateVals[index] = INVALID_DATE;
      setValues(vals);
      setDateValues(dateVals);
    }
  }

  function onCareproviderSearchChanged(event: any) {
    setCareproviderSearch(event.target.value);
    if (event.target.value === "") {
      dispatch({ type: RESET_HSA });
    }
  }

  function searchUnits() {
    const searchString = careproviderSearch;
    dispatch({ type: SEARCH_HSA_UNITS, searchString });
  }

  function onCareProviderSelection(
    event: any,
    value: HSAUnit | null,
    index: any
  ) {
    let providers = [...selectedCareProviders];
    let units = [...selectedCareUnits];
    if (value) {
      providers[index] = value;
      // THIS CASE SHOULD BE VERY RARE
      if (value.careUnits?.length === 0) {
        units[index] = value;
      } else if (value.careUnits?.length === 1) {
        units[index] = value.careUnits[0];
      } else {
        units.splice(index, 1, {});
      }
    } else {
      if (event.type === "click") {
        providers.splice(index, 1, {});
        units.splice(index, 1, {});
      }
    }
    setSelectedCareProviders(providers);
    setSelectedCareUnits(units);
  }

  function onCareUnitSelection(event: any, value: HSAUnit | null, index: any) {
    let units = [...selectedCareUnits];
    let providers = [...selectedCareProviders];

    if (value) {
      units[index] = value;
      if (value.responsibleCareProvider) {
        providers[index] = value.responsibleCareProvider;
      }
    } else {
      if (event.type === "click") {
        units.splice(index, 1, {});
      }
    }

    setSelectedCareUnits(units);
    setSelectedCareProviders(providers);
  }

  function getUnitOptions(index: any) {
    if (selectedCareProviders[index]?.careUnits) {
      if (selectedCareProviders[index]?.careUnits?.length === 0) {
        return [selectedCareProviders[index]];
      } else {
        return selectedCareProviders[index]?.careUnits;
      }
    } else if (careUnits) {
      return careUnits;
    } else {
      return [];
    }
  }

  function getProviderOption(index: any) {
    if (selectedCareUnits[index]?.responsibleCareProvider) {
      return [selectedCareUnits[index]?.responsibleCareProvider];
    } else if (careProviders) {
      return careProviders;
    } else {
      return [];
    }
  }

  function keyDownCheck(event: any) {
    if (event.keyCode === 13) {
      event.preventDefault();
      if (careproviderSearch?.length >= 3) {
        searchUnits();
      }
    }
  }

  function hsaUnitToText(hsaUnit: HSAUnit) {
    if (hsaUnit?.hsaIdentity || hsaUnit?.name) {
      return hsaUnit?.name + " (" + hsaUnit?.hsaIdentity + ")";
    } else {
      return "";
    }
  }

  return (
    <>
      {warningMessages?.map((message: string, i: number) => (
        <Alert sx={{ mt: 0.5 }} key={i} severity="error">
          {message}
        </Alert>
      ))}
      {errorMessages?.map((message: string, i: number) => (
        <Alert sx={{ mt: 0.5 }} key={i} severity="error">
          {message}
        </Alert>
      ))}
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack sx={{ mt: 3, mb: 3, width: 600 }} direction="row" spacing={2}>
          <TextField
            sx={{ width: 250 }}
            id="outlined-careprovider"
            InputLabelProps={{ shrink: true }}
            label={t("searchCard.manageTxns.nameOrHsaId")}
            value={careproviderSearch}
            placeholder={t("searchCard.manageTxns.searchAtLeast")}
            onKeyDown={keyDownCheck}
            onChange={onCareproviderSearchChanged}
          ></TextField>
          <Button
            sx={{ width: 50 }}
            disabled={careproviderSearch.length < 3 || loading}
            onClick={searchUnits}
            variant="contained"
          >
            {t("searchCard.form.submit")}
          </Button>

          {loading ? (
            <CircularProgress />
          ) : (
            <Stack>
              <Typography>
                {careProviders?.length} {t("careProvider")}
              </Typography>

              <Typography>
                {careUnits?.length} {t("careUnits")}
              </Typography>
            </Stack>
          )}
          <Tooltip title={t("searchCard.manageTxns.hsaInfo")}>
            <IconButton>
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Table sx={{ minWidth: 700 }} aria-label="simple table">
          <TableBody>
            {inputList.map((x, i) => {
              return (
                <TableRow
                  style={
                    rowWarnings.some((value: number) => value === i) ||
                    rowErrors.some((value: number) => value === i)
                      ? { border: "2px solid red" }
                      : {}
                  }
                  key={i}
                  sx={{ minWidth: 300 }}
                >
                  <TableCell>
                    <Controller
                      name={amount + i}
                      key={amount + i}
                      control={control}
                      render={() => (
                        <TextField
                          sx={{ width: 150 }}
                          id={amount + i}
                          error={errors[amount + i] ? true : false}
                          label={t("amount") + " *"}
                          {...register("amount" + i, {
                            required: true,
                            validate: isNumber,
                          })}
                        />
                      )}
                    />
                    {errors[amount + i] &&
                      errors[amount + i]?.type === "required" && (
                        <Typography
                          variant="body2"
                          sx={{ color: "#DC143C" }}
                          role="alert"
                        >
                          {t("errors.emptyField")}
                        </Typography>
                      )}
                    {errors[amount + i] &&
                      errors[amount + i]?.type === "validate" && (
                        <Typography
                          variant="body2"
                          sx={{ color: "#DC143C" }}
                          role="alert"
                        >
                          {t("errors.onlyNumbers")}
                        </Typography>
                      )}
                  </TableCell>
                  <TableCell>
                    <LocalizationProvider
                      adapterLocale={sv}
                      dateAdapter={AdapterDateFns}
                    >
                      <DatePicker
                        label={t("date")}
                        value={dateValues[i]}
                        onChange={(keyboardInputValue) => {
                          onDateChange(keyboardInputValue, i);
                        }}
                        rifmFormatter={dateFormatter}
                        renderInput={(params: any) => (
                          // eslint-disable-next-line
                          <TextField
                            sx={{ width: 200 }}
                            value={values[i]}
                            onChange={(event) => {
                              onTextChange(event, i);
                            }}
                            {...params}
                            error={dateValues[i] === INVALID_DATE}
                            helperText={
                              dateValues[i] === INVALID_DATE
                                ? t("errors.invalidDate")
                                : null
                            }
                            inputProps={{
                              ...params.inputProps,
                              placeholder: t("dateFormat"),
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      id="combo-box-demo"
                      options={getProviderOption(i)}
                      sx={{ width: 300 }}
                      getOptionLabel={(option: HSAUnit) =>
                        hsaUnitToText(option)
                      }
                      filterOptions={(options: HSAUnit[], input) =>
                        options?.filter(
                          ({ name, hsaIdentity }) =>
                            name
                              ?.toLowerCase()
                              .includes(input.inputValue.toLowerCase()) ||
                            hsaIdentity
                              ?.toLowerCase()
                              .includes(input.inputValue.toLowerCase())
                        )
                      }
                      defaultValue={null}
                      value={
                        selectedCareProviders[i]
                          ? selectedCareProviders[i]
                          : null
                      }
                      renderOption={(props, hsaUnit: HSAUnit) => (
                        <li {...props}>
                          <Stack>
                            <Typography>{hsaUnit.name}</Typography>
                            <Typography sx={{ fontSize: 11 }}>
                              ({hsaUnit.hsaIdentity})
                            </Typography>
                          </Stack>
                        </li>
                      )}
                      onChange={(event, value: HSAUnit | null) =>
                        onCareProviderSelection(event, value, i)
                      }
                      renderInput={(params) => (
                        <TextField
                          multiline
                          {...params}
                          label={t("careProvider")}
                        />
                      )}
                      ListboxProps={{
                        style: {
                          maxHeight: "200px",
                        },
                      }}
                    />
                    {errors[careProvider + i] &&
                      errors[careProvider + i]?.type === "required" && (
                        <Typography
                          variant="body2"
                          sx={{ color: "#DC143C" }}
                          role="alert"
                        >
                          {t("errors.emptyField")}
                        </Typography>
                      )}
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      id="combo-box-demo"
                      options={getUnitOptions(i)}
                      sx={{ width: 300 }}
                      getOptionLabel={(option: HSAUnit) =>
                        hsaUnitToText(option)
                      }
                      filterOptions={(options: HSAUnit[], input) =>
                        options?.filter(
                          ({ name, hsaIdentity }) =>
                            name
                              ?.toLowerCase()
                              .includes(input.inputValue.toLowerCase()) ||
                            hsaIdentity
                              ?.toLowerCase()
                              .includes(input.inputValue.toLowerCase())
                        )
                      }
                      defaultValue={null}
                      value={selectedCareUnits[i] ? selectedCareUnits[i] : null}
                      renderOption={(props, hsaUnit: HSAUnit) => (
                        <li {...props}>
                          <Stack>
                            <Typography>{hsaUnit.name}</Typography>
                            <Typography sx={{ fontSize: 11 }}>
                              ({hsaUnit.hsaIdentity})
                            </Typography>
                          </Stack>
                        </li>
                      )}
                      onChange={(event, value: HSAUnit | null) =>
                        onCareUnitSelection(event, value, i)
                      }
                      renderInput={(params) => (
                        <TextField
                          multiline
                          {...params}
                          label={t("careUnit")}
                        />
                      )}
                      ListboxProps={{
                        style: {
                          maxHeight: "200px",
                        },
                      }}
                    />
                  </TableCell>
                  {inputList.length !== 1 && i !== 0 && (
                    <TableCell>
                      <Tooltip title={t<string>("remove")}>
                        <IconButton onClick={() => handleRemoveClick(i)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <ListItemButton
          sx={{ mt: 2, width: 150 }}
          alignItems="flex-start"
          onClick={handleAddClick}
        >
          <Stack direction="row" alignItems="center">
            <AddIcon />
            <ListItemText>{t("addField")}</ListItemText>
          </Stack>
        </ListItemButton>
        {warningMessages.length > 0 ? (
          <FormControlLabel
            label="Godkänn"
            control={
              <Checkbox
                checked={ignoreWarning}
                onChange={handleChangeWarning}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />
        ) : null}
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => dispatch({ type: "MODAL_CANCEL" })}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            color="success"
            onKeyPress={(e) => {
              e.key === "Enter" && e.preventDefault();
            }}
            type="submit"
            disabled={!inputList}
          >
            {t("searchCard.manageTxns.addMultipleTransactions")}
          </Button>
        </DialogActions>
      </Box>
    </>
  );
}
